import type React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useImagePerformance } from '../../hooks/useImagePerformance';

export interface LazyImageProps {
  src: string;
  alt: string;
  className?: string;
  loading?: 'lazy' | 'eager';
  decoding?: 'async' | 'sync' | 'auto';
  fallbackSrc?: string;
  webpSrc?: string;
  placeholder?: string;
  width?: number;
  height?: number;
  onLoad?: () => void;
  onError?: () => void;
  'aria-hidden'?: boolean;
  role?: string;
  // biome-ignore lint/suspicious/noExplicitAny: allows arbitrary HTML img attributes to be forwarded
  [key: string]: any;
}

export const LazyImage: React.FC<LazyImageProps> = ({
  src,
  alt,
  className = '',
  loading = 'lazy',
  decoding = 'async',
  fallbackSrc,
  webpSrc,
  placeholder,
  width,
  height,
  onLoad,
  onError,
  ...props
}) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [isIntersecting, setIsIntersecting] = useState(loading === 'eager');
  const [activeSrc, setActiveSrc] = useState(src);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const loadStarted = useRef(false);

  const { handleLoadStart, handleLoadSuccess, handleLoadError } = useImagePerformance(src, {
    onLoad: (metrics) => {
      console.debug(`Image loaded successfully: ${src}`, metrics);
      onLoad?.();
    },
    onError: (metrics) => {
      console.warn(`Image failed to load after ${metrics.retryCount} retries: ${src}`, metrics);
      onError?.();
    },
  });

  // Check WebP support and upgrade source if available
  useEffect(() => {
    if (!webpSrc) {
      return;
    }

    const canvas = document.createElement('canvas');
    canvas.width = 1;
    canvas.height = 1;
    const ctx = canvas.getContext('2d');
    if (ctx) {
      const dataUrl = canvas.toDataURL('image/webp');
      if (dataUrl.indexOf('data:image/webp') === 0) {
        setActiveSrc(webpSrc);
      }
    }
  }, [webpSrc]);

  // Intersection Observer for lazy loading
  useEffect(() => {
    if (loading === 'eager') {
      setIsIntersecting(true);
      return;
    }

    if (!wrapperRef.current) {
      return;
    }

    observerRef.current = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting) {
          setIsIntersecting(true);
          observerRef.current?.disconnect();
        }
      },
      {
        rootMargin: '50px',
        threshold: 0.1,
      },
    );

    observerRef.current.observe(wrapperRef.current);

    return () => {
      observerRef.current?.disconnect();
    };
  }, [loading]);

  // Record load start when the image becomes visible
  useEffect(() => {
    if (isIntersecting && !loadStarted.current) {
      loadStarted.current = true;
      handleLoadStart();
    }
  }, [isIntersecting, handleLoadStart]);

  const handleImgLoad = useCallback(() => {
    setImageLoaded(true);
    setImageError(false);
    handleLoadSuccess();
  }, [handleLoadSuccess]);

  const handleImgError = useCallback(() => {
    if (fallbackSrc && activeSrc !== fallbackSrc) {
      setActiveSrc(fallbackSrc);
    } else {
      setImageError(true);
      handleLoadError();
    }
  }, [fallbackSrc, activeSrc, handleLoadError]);

  // Determine whether to set the real src on the <img>
  const imgSrc = isIntersecting ? activeSrc : undefined;

  return (
    <div
      ref={wrapperRef}
      className={`lazy-image-wrapper ${className} ${imageLoaded ? 'lazy-image-wrapper--loaded' : ''}`}
      {...props}
    >
      {/* Placeholder layer — visible until the real image loads */}
      {!imageLoaded && (
        <div
          className="lazy-image-placeholder"
          style={{
            backgroundImage: placeholder ? `url(${placeholder})` : undefined,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        >
          {!(placeholder || imageError) && (
            <output className="loading-spinner" aria-live="polite">
              <span className="sr-only">Loading image</span>
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{ animation: 'spin 1s linear infinite' }}
                aria-hidden="true"
              >
                <path d="M21 12a9 9 0 11-6.219-8.56" />
              </svg>
            </output>
          )}
          {imageError && (
            <div className="image-error">
              <span className="sr-only">Failed to load image</span>
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                <circle cx="8.5" cy="8.5" r="1.5" />
                <polyline points="21,15 16,10 5,21" />
              </svg>
            </div>
          )}
        </div>
      )}

      {/* Always in the DOM so its intrinsic size drives the wrapper layout */}
      <img
        src={imgSrc}
        alt={alt}
        className={`lazy-image ${imageLoaded ? 'loaded' : ''}`}
        loading={loading}
        decoding={decoding}
        onLoad={imgSrc ? handleImgLoad : undefined}
        onError={imgSrc ? handleImgError : undefined}
        width={width}
        height={height}
      />
    </div>
  );
};
